import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid } from 'semantic-ui-react';
import { UpsertTreatmentReasonDto } from 'ts-types/api.types';
import { addTreatmentReason, editTreatmentReason } from 'services/treatmentReasonServices';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import Select from 'components/final-form/Select';
import { required } from 'utils/validatorUtils';
import { XmlReason } from 'ts-types/api.enums';
import axios, { CancelTokenSource } from 'axios';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  treatmentReasonId?: number,
  treatmentReason?: UpsertTreatmentReasonDto
}

interface State {
  errorMessage?: string,
  xmlReasons: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class UpsertTreatmentReasonsForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;

    const xmlReasonKeys = Object.keys(XmlReason);

    const cancelTokenSource = axios.CancelToken.source();

    const mappedXmlReasons: Array<DropdownOption> = xmlReasonKeys.map((reason, index) => {
      return {
        key: index,
        text: getMessage(reason.toLowerCase().replace("_", "")),
        value: reason
      }
    });

    this.state = {
      xmlReasons: mappedXmlReasons,
      cancelTokenSource
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  handleSubmit = async (values: Partial<UpsertTreatmentReasonDto>) => {

    let saveValues: Partial<UpsertTreatmentReasonDto> = {...values};

    const {treatmentReasonId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (treatmentReasonId) {
      await editTreatmentReason(treatmentReasonId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addTreatmentReason(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const defaultValues: Partial<UpsertTreatmentReasonDto> = {
      basisReason: false,
      xmlKey: XmlReason.UNKNOWN
    };

    const {treatmentReason = defaultValues} = this.props;

    return (
        <FinalForm
            onSubmit={(values => this.handleSubmit(values))}
            initialValues={treatmentReason}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {xmlReasons, errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("treatmentReason.description")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="description"
                        component={Input}
                        validate={required}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("treatmentReason.descriptionfr")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="descriptionFR"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("treatmentReason.descriptionit")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="descriptionIT"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("treatmentReason.descriptionen")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="descriptionEN"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("treatmentReason.xmlkey")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="xmlKey"
                        component={Select}
                        options={xmlReasons}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <Field
                        name="basisReason"
                        component={CheckBox}
                        label={`${getMessage("treatmentReason.basisreason")}`}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertTreatmentReasonsForm);
